import "./KaboodleSwimlanes.scss";

import LoadingIcon from "components/Loading/LoadingIcon";
import SwimLaneHeader from "components/SwimLaneHeader/SwimLaneHeader.tsx";
import { useToken } from "utils/context.ts";
import { useCurrentUser, useQuery } from "utils/hooks";
import { GLOBAL_SEARCH_PARAM, useUrlStore } from "utils/useUrlStore";

import { useTaskBoardData } from "../../hooks/useTaskBoardData";
import { useTaskModalState } from "../../hooks/useTaskModalState";
import type { TaskStatus } from "../../utils/taskUtils";
import { KaboodleSwimLaneColumn } from "./KaboodleSwimLaneColumn";
import TaskBoardModals from "./TaskBoardModals";

const SWIMLANE_COLUMNS: Array<{ status: TaskStatus; title: string }> = [
  { status: "P", title: "Pending" },
  { status: "I", title: "In Progress" },
  { status: "C", title: "Complete" },
];

/* render the task board and delegate data/mutation orchestration to hooks. */
export const KaboodleSwimlanes = () => {
  const { getFilters } = useUrlStore();
  const filters = getFilters();
  const { accessToken } = useToken();
  const searchTerm = useQuery().get(GLOBAL_SEARCH_PARAM) || "";
  const user = useCurrentUser();
  const {
    handleCloseErrorModal,
    handleCloseSendToOpsModal,
    handleCloseTaskModal,
    showErrorModal,
    showSendToOpsModal,
    showTaskModal,
  } = useTaskModalState();

  const {
    completeTasks,
    errors,
    inProgressTasks,
    isLoading,
    isRefetching,
    items,
    moveItem,
    pendingTasks,
  } = useTaskBoardData({
    filters,
    searchTerm,
    userId: user?.id,
    accessToken,
  });

  if (!accessToken || !user?.id) {
    return null;
  }

  if (isLoading) {
    return <LoadingIcon />;
  }

  return (
    <>
      <TaskBoardModals
        errors={errors}
        onCloseErrorModal={handleCloseErrorModal}
        onCloseSendToOpsModal={handleCloseSendToOpsModal}
        onCloseTaskModal={handleCloseTaskModal}
        showErrorModal={showErrorModal}
        showSendToOpsModal={showSendToOpsModal}
        showTaskModal={showTaskModal}
      />
      <div className="swim-wrapper h-100">
        <div className="grid-layout no-gap-tb your-tasks-3 swimlanes">
          <SwimLaneHeader label="To Do" />
          <SwimLaneHeader label="In Progress" />
          <SwimLaneHeader label="Complete" />

          {SWIMLANE_COLUMNS.map(({ status, title }) => {
            const queryData =
              status === "P"
                ? pendingTasks
                : status === "I"
                  ? inProgressTasks
                  : completeTasks;

            return (
              <KaboodleSwimLaneColumn
                key={status}
                title={title}
                items={items[status]}
                columnId={status}
                moveItem={moveItem}
                hasNextPage={queryData.hasNextPage}
                isFetchingNextPage={queryData.isFetchingNextPage}
                fetchNextPage={
                  queryData.fetchNextPage as unknown as () => Promise<void>
                }
                isRefetching={isRefetching}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};

export default KaboodleSwimlanes;
